import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withFetch,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  Meta,
  provideClientHydration,
  Title,
  withIncrementalHydration,
} from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
  provideRouter,
  UrlSerializer,
  withInMemoryScrolling,
  withNavigationErrorHandler,
} from '@angular/router';
import { provideEffects } from '@ngrx/effects';
import { provideStore } from '@ngrx/store';
import { NgbModalModule } from '@ng-bootstrap/ng-bootstrap/modal';
import { CookieModule } from 'ngx-cookie';
import { provideToastr } from 'ngx-toastr';

import { routes } from './app.routes';
import { provideFontAwesomeIcons } from './core/font-awesome-icons';
import { GlobalErrorHandlerService } from './core/services/global-error-handler.service';
import { TrailingSlashUrlSerializer } from './shared/functions/trailing-slash-url-serializer';
import { HeaderInterceptor } from './shared/http-interceptors/header-interceptor';
import { PermissionsEffects } from './stores/access-control/access-control.effects';
import { PermissionsReducer } from './stores/access-control/access-control.reducers';
import { globalReducer } from './stores/global/global.reducer';
import { layoutReducer } from './stores/layout/layout.reducer';
import { sidebarMetaReducer } from './stores/sidebar/sidebar.meta-reducer';
import { sidebarReducer } from './stores/sidebar/sidebar.reducer';
import { WatchlistSymbolsEffects } from './stores/watchlist-symbols/watchlist-symbols.effects';
import { watchlistSymbolsReducer } from './stores/watchlist-symbols/watchlist-symbols.reducers';

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideFontAwesomeIcons(),
    provideRouter(
      routes,
      withNavigationErrorHandler((event) =>
        inject(GlobalErrorHandlerService).handleNavigationError(event),
      ),
      withInMemoryScrolling({
        scrollPositionRestoration: 'disabled',
        anchorScrolling: 'enabled',
      }),
    ),
    provideClientHydration(withIncrementalHydration()),
    provideHttpClient(withFetch(), withInterceptorsFromDi()),
    provideAnimationsAsync(),
    provideToastr({
      positionClass: 'toast-top-right',
      preventDuplicates: true,
    }),
    provideStore(
      {
        watchlistSymbols: watchlistSymbolsReducer,
        permissions: PermissionsReducer,
        sidebar: sidebarReducer,
        layout: layoutReducer,
        global: globalReducer,
      },
      {
        metaReducers: [sidebarMetaReducer],
        runtimeChecks: {
          strictStateImmutability: true,
          strictActionImmutability: true,
        },
      },
    ),
    provideEffects(WatchlistSymbolsEffects, PermissionsEffects),
    importProvidersFrom(CookieModule.withOptions(), NgbModalModule),
    { provide: HTTP_INTERCEPTORS, useClass: HeaderInterceptor, multi: true },
    { provide: ErrorHandler, useExisting: GlobalErrorHandlerService },
    { provide: UrlSerializer, useClass: TrailingSlashUrlSerializer },
    Title,
    Meta,
  ],
};
